import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeftRight,
  AlertCircle,
  CheckCircle2,
  Clock,
  ShieldCheck,
  History,
  Lock,
  Phone,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import PopularBadge from "@/components/common/PopularBadge";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const ASSETS = ["USDT", "BTC"];
const DEFAULT_MIN_INR = 1_000_000;

function fmtINR(v) {
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  });
}

function fmtAsset(v) {
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("en-US", { maximumFractionDigits: 8 });
}

export default function OtcPage() {
  const { user } = useAuth();
  const kycApproved = user?.kyc_status === "approved" || user?.role === "admin";

  const [side, setSide] = useState("buy");
  const [asset, setAsset] = useState("USDT");
  const [amountType, setAmountType] = useState("fiat");
  const [amount, setAmount] = useState("");

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [submitted, setSubmitted] = useState(null);
  const [history, setHistory] = useState([]);
  const [minTradeInr, setMinTradeInr] = useState(DEFAULT_MIN_INR);

  const aliveRef = useRef(true);

  useEffect(() => () => { aliveRef.current = false; }, []);

  useEffect(() => {
    api.get("/api/otc/settings")
      .then(({ data }) => {
        if (data?.min_trade_inr) setMinTradeInr(data.min_trade_inr);
      })
      .catch(() => {});
  }, []);

  async function fetchHistory() {
    try {
      const { data } = await api.get("/api/otc/history");
      return data || [];
    } catch (_) {}
    return null;
  }

  async function loadHistory() {
    const items = await fetchHistory();
    if (!aliveRef.current || !items) return;
    setHistory(items);
    const open = items.find((h) => ["pending", "contacted", "processing"].includes(h.status));
    if (open) {
      setSubmitted(open);
    } else if (submitted) {
      const latest = items.find((h) => h.quote_id === submitted.quote_id);
      if (latest) setSubmitted(latest);
    }
  }

  useEffect(() => {
    if (kycApproved) loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kycApproved]);

  const openRequest = useMemo(
    () => history.find((h) => ["pending", "contacted", "processing"].includes(h.status)),
    [history],
  );

  const activeRequest = useMemo(() => {
    if (submitted) {
      return history.find((h) => h.quote_id === submitted.quote_id) || submitted;
    }
    return openRequest;
  }, [submitted, history, openRequest]);

  const showRequestPanel =
    activeRequest &&
    ["pending", "contacted", "processing", "confirmed"].includes(activeRequest.status);

  const shouldPoll = useMemo(
    () => openRequest || (submitted && ["pending", "contacted", "processing"].includes(submitted.status)),
    [openRequest, submitted],
  );

  useEffect(() => {
    if (!kycApproved || !shouldPoll) return undefined;
    const id = setInterval(loadHistory, 12000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kycApproved, shouldPoll, submitted?.quote_id, submitted?.status, openRequest?.quote_id]);

  const fillBlocked = useMemo(() => {
    if (!amount || Number(amount) <= 0) return "Enter an amount";
    if (amountType === "fiat" && Number(amount) < minTradeInr) {
      return `Minimum OTC trade size is ₹${minTradeInr.toLocaleString("en-IN")}`;
    }
    return null;
  }, [amount, amountType, minTradeInr]);

  async function submitRequest(e) {
    e.preventDefault();
    if (fillBlocked) return setErr(fillBlocked);
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/api/otc/quote", {
        side,
        asset,
        amount_type: amountType,
        amount: Number(amount),
      }, { timeout: 30000 });
      setSubmitted(data.request);
      setAmount("");
      loadHistory();
    } catch (e2) {
      setErr(getErrorMessage(e2));
    } finally {
      setLoading(false);
    }
  }

  async function cancelRequest(quoteId) {
    try {
      await api.post(`/api/otc/quote/${quoteId}/cancel`);
    } catch (_) {}
    setSubmitted(null);
    loadHistory();
  }

  if (user && !kycApproved) {
    return (
      <AppShell>
        <div className="max-w-[640px] mx-auto bg-white rounded-[20px] p-10 border border-[var(--cm-border)] text-center" data-testid="otc-kyc-required">
          <div className="w-16 h-16 mx-auto rounded-full bg-amber-50 grid place-items-center mb-5">
            <Lock className="text-amber-600" />
          </div>
          <h1 className="text-[24px] font-bold text-cm-navy">KYC required for OTC</h1>
          <p className="mt-3 text-[14px] text-cm-grey">
            Per FIU-IND regulations, OTC trading is only available to KYC-approved users.
            Complete your KYC and our desk will be ready to take your first request.
          </p>
          <Link
            to="/kyc"
            className="mt-6 inline-flex items-center justify-center gap-2 h-[48px] px-7 rounded-[12px] bg-cm-blue text-white font-bold hover:bg-cm-blue-deep transition"
          >
            Complete KYC <ArrowRight size={16} />
          </Link>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell maxWidth="1100px">
      <div className="mb-8">
        <span className="inline-flex items-center gap-2">
          <PopularBadge testId="otc-page-popular-badge" />
          <span className="inline-block text-[10px] font-bold tracking-[0.25em] uppercase text-cm-blue bg-blue-50 px-2.5 py-1 rounded">
            OTC Desk
          </span>
        </span>
        <h1 className="mt-3 text-[32px] font-bold text-cm-navy tracking-tight">
          {side === "buy" ? "Buy" : "Sell"} USDT & BTC
        </h1>
        <p className="mt-2 text-[14px] text-cm-grey">
          OTC desk — submit a request for buy or sell. Locked-in INR pricing.
        </p>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="bg-white rounded-[18px] p-7 border border-[var(--cm-border)]">
          {showRequestPanel ? (
            <RequestPending
              request={activeRequest}
              onCancel={cancelRequest}
              onNew={() => setSubmitted(null)}
            />
          ) : (
            <form onSubmit={submitRequest} className="space-y-5" data-testid="otc-rfq-form">
              <div className="grid grid-cols-2 gap-2">
                {["buy", "sell"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSide(s)}
                    data-testid={`rfq-side-${s}`}
                    className={`py-3 rounded-[10px] text-[14px] font-bold capitalize transition ${
                      side === s
                        ? s === "buy"
                          ? "bg-emerald-600 text-white"
                          : "bg-red-500 text-white"
                        : "bg-cm-surface text-cm-grey hover:text-cm-navy"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>

              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cm-surface border border-[var(--cm-border)]">
                <span className="text-[11px] font-bold uppercase tracking-wider text-cm-navy">
                  {side === "buy" ? "Buy crypto with INR" : "Sell crypto for INR"}
                </span>
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-cm-navy mb-2">Asset</label>
                <div className="grid grid-cols-2 gap-2">
                  {ASSETS.map((a) => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => setAsset(a)}
                      data-testid={`rfq-asset-${a.toLowerCase()}`}
                      className={`py-3 rounded-[10px] text-[14px] font-bold transition ${
                        asset === a
                          ? "bg-cm-navy text-white"
                          : "bg-cm-surface text-cm-grey hover:text-cm-navy"
                      }`}
                    >
                      {a}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[12px] font-semibold text-cm-navy">Amount</label>
                  <button
                    type="button"
                    onClick={() => setAmountType((t) => (t === "fiat" ? "asset" : "fiat"))}
                    data-testid="rfq-toggle-amount-type"
                    className="text-[11px] text-cm-blue font-semibold hover:underline flex items-center gap-1"
                  >
                    <ArrowLeftRight size={11} />
                    {amountType === "fiat" ? `Switch to ${asset}` : "Switch to ₹"}
                  </button>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
                    placeholder={amountType === "fiat" ? "10,00,000" : `0.5 ${asset}`}
                    data-testid="rfq-amount"
                    className="w-full h-[60px] px-4 pr-20 border border-[#E0E3EF] rounded-[12px] text-[20px] font-bold focus:border-cm-blue outline-none"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[14px] font-bold text-cm-grey">
                    {amountType === "fiat" ? "₹" : asset}
                  </span>
                </div>
              </div>

              {err && (
                <div className="flex items-start gap-2 text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-[10px] px-4 py-3">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{err}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="rfq-get-quote"
                className="w-full h-[58px] rounded-[12px] bg-cm-navy text-white font-bold text-[15px] hover:bg-cm-blue transition flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? "Submitting request…" : "Submit request"}
                {!loading && <ArrowRight size={16} />}
              </button>
            </form>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-[18px] p-6 border border-[var(--cm-border)]">
            <h3 className="text-[14px] font-bold text-cm-navy flex items-center gap-2">
              <ShieldCheck size={15} className="text-cm-blue" />
              How OTC works
            </h3>
            <ol className="mt-3 space-y-2.5 text-[13px] text-cm-grey leading-relaxed">
              <li className="flex gap-2">
                <span className="font-bold text-cm-navy">1.</span>
                Choose USDT or BTC and enter your desired INR amount.
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-cm-navy">2.</span>
                A dedicated Relationship Manager (RM) will contact you, provide a live quote, and assist you in reviewing and confirming the price.
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-cm-navy">3.</span>
                Once confirmed, your trade is executed and the assets are credited directly to your wallet.
              </li>
            </ol>
          </div>

          <div
            className="rounded-[18px] p-6 text-white"
            style={{ background: "linear-gradient(135deg, #1a2547 0%, #0c1224 100%)" }}
          >
            <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-amber-300 mb-2">
              Need a custom deal?
            </div>
            <p className="text-[13px] text-white/70 leading-relaxed">
              Buy USDT and BTC in size — off-exchange execution for treasuries, family offices, and HNIs.
              Move ₹10 L to ₹5 Cr+ in a single ticket without slippage on the public order book.
            </p>
            <a
              href="https://wa.me/918796101200"
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-bold text-amber-300 hover:text-amber-200"
            >
              Chat with desk ➡
            </a>
          </div>
        </div>
      </div>

      <div className="mt-10">
        <h2 className="text-[18px] font-bold text-cm-navy flex items-center gap-2 mb-4">
          <History size={16} /> Recent OTC activity
        </h2>
        <div className="bg-white rounded-[16px] border border-[var(--cm-border)] overflow-hidden">
          {history.length === 0 ? (
            <div className="p-10 text-center text-cm-grey text-[13px]" data-testid="otc-history-empty">
              No OTC activity yet.
            </div>
          ) : (
            <table className="w-full text-[13px]">
              <thead className="bg-cm-surface text-cm-grey text-left">
                <tr>
                  <th className="px-5 py-3 font-semibold">Date</th>
                  <th className="px-5 py-3 font-semibold">Side</th>
                  <th className="px-5 py-3 font-semibold">Asset</th>
                  <th className="px-5 py-3 font-semibold text-right">Quantity</th>
                  <th className="px-5 py-3 font-semibold text-right">Price (₹)</th>
                  <th className="px-5 py-3 font-semibold text-right">Total (₹)</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--cm-border)]">
                {history.map((h) => (
                  <tr key={h.quote_id} data-testid={`otc-history-row-${h.quote_id}`}>
                    <td className="px-5 py-3 text-cm-grey">
                      {new Date(h.created_at).toLocaleString("en-IN", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`text-[11px] font-bold uppercase px-2 py-1 rounded ${
                          h.side === "buy"
                            ? "bg-emerald-50 text-emerald-600"
                            : "bg-red-50 text-red-500"
                        }`}
                      >
                        {h.side}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-bold text-cm-navy">{h.asset}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-cm-navy">
                      {h.asset_qty != null ? fmtAsset(h.asset_qty) : "—"}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums text-cm-grey">
                      {h.quoted_price_inr != null ? fmtINR(h.quoted_price_inr) : "—"}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums font-bold text-cm-navy">
                      {h.fiat_total_inr != null ? fmtINR(h.fiat_total_inr) : "—"}
                    </td>
                    <td className="px-5 py-3">
                      <StatusPill status={h.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: ["bg-amber-50", "text-amber-600", "Pending"],
    contacted: ["bg-blue-50", "text-cm-blue", "RM contacted"],
    processing: ["bg-blue-50", "text-cm-blue", "Processing"],
    confirmed: ["bg-emerald-50", "text-emerald-600", "Confirmed"],
    expired: ["bg-cm-surface", "text-cm-grey", "Expired"],
    cancelled: ["bg-cm-surface", "text-cm-grey", "Cancelled"],
    rejected: ["bg-red-50", "text-red-500", "Rejected"],
  };
  const [bg, color, label] = map[status] || ["bg-cm-surface", "text-cm-grey", status];
  return (
    <span className={`text-[11px] font-bold uppercase px-2 py-1 rounded ${bg} ${color}`}>
      {label}
    </span>
  );
}

function RequestPending({ request, onCancel, onNew }) {
  const isOpen = ["pending", "contacted", "processing"].includes(request.status);

  if (request.status === "confirmed") {
    return (
      <div className="text-center py-6" data-testid="otc-trade-success">
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-50 grid place-items-center mb-5">
          <CheckCircle2 size={32} className="text-emerald-500" />
        </div>
        <h3 className="text-[22px] font-bold text-cm-navy">Trade confirmed</h3>
        <p className="mt-2 text-[13px] text-cm-grey">
          {request.side === "buy" ? (
            <>
              Bought{" "}
              <strong className="text-cm-navy">
                {fmtAsset(request.asset_qty)} {request.asset}
              </strong>{" "}
              — credited to your wallet.
            </>
          ) : (
            <>
              Sold{" "}
              <strong className="text-cm-navy">
                {fmtAsset(request.asset_qty)} {request.asset}
              </strong>{" "}
              for{" "}
              <strong className="text-cm-navy">{fmtINR(request.net_fiat_inr)}</strong>{" "}
              — INR credited to your wallet.
            </>
          )}
        </p>
        <button
          onClick={onNew}
          className="mt-6 inline-flex items-center justify-center gap-2 h-[48px] px-7 rounded-[12px] bg-cm-navy text-white font-bold hover:bg-cm-blue transition"
        >
          New request <ArrowRight size={16} />
        </button>
      </div>
    );
  }

  return (
    <div data-testid="otc-request-pending">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-12 h-12 rounded-full bg-amber-50 grid place-items-center shrink-0">
          <Clock size={22} className="text-amber-600" />
        </div>
        <div>
          <h3 className="text-[20px] font-bold text-cm-navy">Request submitted</h3>
          <p className="mt-1 text-[13px] text-cm-grey leading-relaxed">
            Your {request.side} request for {request.asset} is{" "}
            <StatusPill status={request.status} />.
            A Relationship Manager will contact you with a live quote before execution.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-y-3 text-[13px] bg-cm-surface rounded-[12px] p-5 mb-5">
        <Cell label="Side" value={request.side?.toUpperCase()} />
        <Cell label="Asset" value={request.asset} />
        <Cell label="Indicative price" value={request.quoted_price_inr != null ? fmtINR(request.quoted_price_inr) : "Pending RM quote"} />
        <Cell label="Indicative qty" value={request.asset_qty != null ? `${fmtAsset(request.asset_qty)} ${request.asset}` : "—"} />
        <Cell label="Indicative total" value={request.fiat_total_inr != null ? fmtINR(request.fiat_total_inr) : "—"} big />
      </div>

      <div className="flex items-start gap-2 text-[12px] text-cm-blue bg-blue-50 border border-blue-100 rounded-[10px] px-4 py-3 mb-5">
        <Phone size={14} className="shrink-0 mt-0.5" />
        <span>Our desk will reach out on your registered email and mobile. Final pricing is confirmed by your RM.</span>
      </div>

      <div className="flex gap-3">
        {isOpen && (
          <button
            onClick={() => onCancel(request.quote_id)}
            data-testid="otc-request-cancel"
            className="flex-1 h-[48px] rounded-[12px] bg-white border border-[var(--cm-border)] text-cm-navy font-bold hover:bg-cm-surface transition"
          >
            Cancel request
          </button>
        )}
        <Link
          to="/wallets"
          className="flex-1 h-[48px] rounded-[12px] bg-cm-navy text-white font-bold hover:bg-cm-blue transition grid place-items-center"
        >
          View wallet
        </Link>
      </div>
    </div>
  );
}

function Cell({ label, value, big }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-cm-grey">{label}</div>
      <div className={`tabular-nums ${big ? "text-[18px] font-bold text-cm-navy" : "text-[14px] font-semibold text-cm-navy"}`}>
        {value}
      </div>
    </div>
  );
}
